import { useState, useEffect } from "react";
import { 
    Stack, 
    Spinner, 
    SpinnerSize, 
    PrimaryButton,
    TextField,
    Label,
    mergeStyles,
} from "@fluentui/react";
import { 
    getContainerList, 
    createContainer, 
} from "../../../api/admin/api_admin";
import { ContainerListData } from "../../../api/admin/models_admin";
import { AdminLabel, ErrorDialog, ConfirmDialog } from "../components";
import { UploadAccordionElement } from "./upload_accordion_element";

const instructionsStyle = mergeStyles({
    padding: '16px',
    backgroundColor: '#f3f2f1',
    borderRadius: '4px',
    marginBottom: '16px'
});

const categoryStyle = mergeStyles({
    color: '#faa550',
    fontWeight: 600
});

export const Upload = () => {
    const [listData, setListData] = useState<ContainerListData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [expandingSeqs, setExpandingSeqs] = useState<Set<number>>(new Set<number>())
    
    // 新規カテゴリ作成用
    const [newCategoryName, setNewCategoryName] = useState<string>("");
    const [creating, setCreating] = useState<boolean>(false);

    useEffect(() => {
        fetchUploadData();
    }, []);

    const fetchUploadData = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getContainerList();
            if ("error" in data) {
                setError(data.error);
            } else {
                setListData(data);
            }
        } catch (err) {
            setError("データの取得中にエラーが発生しました");
        } finally {
            setLoading(false);
        }
    };

    const handleCreateCategory = async () => {
        if (!newCategoryName.trim()) {
            setError("カテゴリ名を入力してください");
            return;
        }

        setCreating(true);
        setError(null);
        try {
            const result = await createContainer({ container_name: newCategoryName });
            if (result.success) {
                setSuccessMessage("カテゴリの作成に成功しました");
                setNewCategoryName("");
                await fetchUploadData();
            } else {
                setError(result.message || "カテゴリの作成に失敗しました");
            }
        } catch (err) {
            setError("カテゴリの作成中にエラーが発生しました");
        } finally {
            setCreating(false);
        }
    };

    if (loading) {
        return (
            <Stack horizontalAlign="center" verticalAlign="center" style={{ minHeight: '400px' }}>
                <Spinner size={SpinnerSize.large} label="読み込み中..." />
            </Stack>
        );
    }

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <AdminLabel text="文書検索設定・ファイルアップロード" />

            {
                <ErrorDialog 
                    content={error ?? ""} 
                    onDismiss={() => setError(null)} 
                    isOpen={Boolean(error)} />
            }

            {
                <ConfirmDialog 
                    content={successMessage ?? ""} 
                    onDismiss={() => setSuccessMessage(null)} 
                    isOpen={Boolean(successMessage)}
                    title="確認" />
            }

            {/* 使用方法の説明 */}
            <div className={instructionsStyle}>
                <Stack tokens={{ childrenGap: 12 }}>
                    <Label styles={{ root: { fontWeight: 'bold' } }}>
                        文書検索を行うには、以下の手順で設定を行ってください。
                    </Label>
                    <ol style={{ margin: 0, paddingLeft: 20 }}>
                        <li>
                            <span className={categoryStyle}>カテゴリ作成</span>より、カテゴリを作成します
                        </li>
                        <li>
                            <span className={categoryStyle}>カテゴリ一覧</span>の「ファイル選択」でファイルを選択(複数可)し、「アップロード」でファイルをアップロードします
                        </li>
                        <li>
                            <span className={categoryStyle}>カテゴリ一覧</span>で「文書検索設定」の「設定する」を押下します
                        </li>
                    </ol>
                    <Label>※文書は後から追加・変更・削除が可能です。</Label>
                </Stack>
            </div>

            <div className={instructionsStyle}>
                <Stack tokens={{ childrenGap: 12 }}>
                    <Label styles={{ root: { fontWeight: 'bold' } }}>使用しなくなった場合</Label>
                    <Stack tokens={{ childrenGap: 8 }}>
                        <Label>
                            カテゴリを文書検索で使用しなくなった場合は、まず文書検索設定の解除を行います。
                        </Label>
                        <Label>
                            その後カテゴリを削除します。
                        </Label>
                        <Label>
                            カテゴリを削除せずに保存しておき、その後再び文書検索設定を行って再使用することも可能です。
                        </Label>
                    </Stack>
                </Stack>
            </div>

            {/* カテゴリ一覧 */}
            <Stack tokens={{ childrenGap: 12 }}>
                <AdminLabel text="カテゴリ一覧" />
                {listData && listData.containers.length > 0 ? (
                    <div>
                        {listData.containers.map((container, index) => 
                        <UploadAccordionElement 
                            key={index}
                            containerListInfo={container}
                            isFirst={index === 0}
                            isLast={listData.containers ? index === listData.containers.length - 1 : false}
                            onError={setError}
                            onSuccess={setSuccessMessage}
                            updateList={fetchUploadData}
                            isExpanded={expandingSeqs.has(container.seq)}
                            setIsExpanded={(value) => {
                                if (value) {
                                    expandingSeqs.add(container.seq)
                                }
                                else{
                                    expandingSeqs.delete(container.seq)
                                }
                                setExpandingSeqs(new Set(expandingSeqs))
                            }}
                        />)}
                    </div>
                ) : (
                    <Label>
                        カテゴリがありません。文書検索を行うには、文書をアップロードするカテゴリを作成してください。
                    </Label>
                )}
                <Label>
                    ※文書検索設定済のカテゴリは削除できません。文書検索設定を解除してから削除してください。
                </Label>
                <Label>
                    ※文書検索設定には時間がかかる場合があります。
                </Label>
            </Stack>

            {/* カテゴリ作成 */}
            <Stack tokens={{ childrenGap: 12 }}>
                <AdminLabel text="カテゴリ作成" />
                <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="end">
                    <TextField
                        label="カテゴリ名"
                        value={newCategoryName}
                        onChange={(_, newValue) => setNewCategoryName(newValue || "")}
                        placeholder="カテゴリ名"
                        required
                        styles={{ root: { width: 300 } }}
                    />
                    <PrimaryButton 
                        text="カテゴリ作成" 
                        onClick={handleCreateCategory}
                        disabled={creating || !newCategoryName.trim()}
                    />
                </Stack>
            </Stack>
        </Stack>
    );
}
